import {
  ArrowForwardIcon,
  Box,
  ListItemButton,
  ListItemText,
  makeStyles,
  Theme,
  Typography,
} from '@freighthub/silhouette-core'
import { placeToString, PortOrAddress, portOrAddressToString } from 'features/Directory'
import { TransportModeIcon } from 'features/TransportPlan'
import { FC, MouseEvent } from 'react'
import { useI18n } from 'utils/localisation'

import { IBookingTemplateWithLocation } from '../BookingTemplate'
import { PointJSON } from '../Point'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    height: theme.spacing(7),
    padding: theme.spacing(0, 2),
    cursor: 'pointer',
    overflow: 'hidden',
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '&:hover, &:focus': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  title: {
    fontWeight: theme.typography.fontWeightMedium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dot: {
    margin: theme.spacing(0, 0.75),
  },
  arrow: {
    height: 16,
    width: 16,
    margin: theme.spacing(0, 0.5),
  },
  icon: {
    height: '1.25rem',
    width: '1.25rem',
    marginLeft: theme.spacing(1),
  },
  primary: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  secondary: {
    display: 'flex',
    alignItems: 'center',
  },
  multiline: {
    margin: 0,
  },
}))

interface BookingTemplatesListItemProps {
  template: IBookingTemplateWithLocation
  onClick: (event: MouseEvent<HTMLDivElement>, template: IBookingTemplateWithLocation) => void
}
export const BookingTemplatesListItem: FC<BookingTemplatesListItemProps> = ({ template, onClick }) => {
  const {
    modes,
    name,
    createdAt,
    updatedAt,
    originLocation,
    destinationLocation,
    journey: { origin, destination },
  } = template
  const classes = useStyles({})
  const { current, translate } = useI18n()

  const originString = getLocationString(origin, originLocation)
  const destinationString = getLocationString(destination, destinationLocation)
  const dateLabel = updatedAt ? translate('booking-history_updated_on') : translate('booking-history_created_on')
  const displayedDate = new Date(updatedAt || createdAt).toLocaleDateString(current)

  const primaryText = (
    <>
      <Typography component="span" color="textPrimary" className={classes.title}>
        {name}
      </Typography>
      <span className={classes.dot}>·</span>
      <span>
        {dateLabel} {displayedDate}
      </span>
    </>
  )

  const secondaryText = (
    <>
      <Box component="span" display="flex" alignItems="center" overflow="hidden">
        <Box component="span" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {originString}
        </Box>
        <ArrowForwardIcon className={classes.arrow} />
        <Box component="span" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {destinationString}
        </Box>
      </Box>
      {modes.map((mode) => (
        <TransportModeIcon key={mode} transportMode={mode} className={classes.icon} />
      ))}
    </>
  )

  return (
    <ListItemButton className={classes.root} onClick={(event) => onClick(event, template)}>
      <ListItemText
        primary={primaryText}
        secondary={secondaryText}
        primaryTypographyProps={{
          variant: 'caption',
          color: 'textSecondary',
        }}
        secondaryTypographyProps={{
          variant: 'caption',
          color: 'textSecondary',
        }}
        classes={{
          primary: classes.primary,
          secondary: classes.secondary,
          multiline: classes.multiline,
        }}
      />
    </ListItemButton>
  )
}

type Location = IBookingTemplateWithLocation['originLocation' | 'destinationLocation']

const getLocationString = (portOrAddress: PointJSON, location: Location) => {
  if (location) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `${location.name}, ${placeToString(location.place)}`
  }

  if (portOrAddress) {
    return portOrAddressToString(portOrAddress as PortOrAddress)
  }

  return ''
}
